import { getData, updateData, updateLastLoginTime } from './firestoreService';
import { logAuditEventAsync, AuditActions } from './auditLogger';
import { isProfileComplete } from './userProfile';

/**
 * Handles login operations for both email and Google authentication
 * @param {Object} user - Firebase user object
 * @param {string} loginType - Type of login ('email' or 'google')
 * @param {Function} setSuccess - Function to set success message
 * @param {Function} navigate - Navigation function
 * @param {Function} setError - Function to set error message (optional)
 * @returns {Promise<boolean>} - Returns false if login should be blocked
 */
export const firstLogin = async (user, loginType, setSuccess, navigate, setError = null) => {
  try {
    // Check if user account is disabled
    const userData = await getData(`users/${user.uid}`);
    if (userData?.disabled === true) {
      // Sign out the user
      const { auth } = await import('./firebase');
      await auth.signOut();
      
      // Audit log: login failure (account disabled)
      logAuditEventAsync(AuditActions.AUTH_LOGIN_FAILURE, {
        userEmail: user.email,
        userId: user.uid,
        metadata: { reason: 'account_disabled' },
      });
      
      if (setError) {
        setError("Your account has been deactivated. Please contact your administrator.");
      }
      return false;
    }

    const userUpdates = {
      lastLoginTime: new Date().toISOString(),
      sessionStartTime: new Date().toISOString(),
    };

    if (loginType === 'google') {
      userUpdates.provider = 'google';
      
      // Explicit clinician names take precedence over the Google display name.
      if (user.displayName && !isProfileComplete(userData)) {
        userUpdates.displayName = user.displayName;
      }
      if (user.photoURL) {
        userUpdates.photoURL = user.photoURL;
      }
    }

    await updateData('users', user.uid, userUpdates);
    await updateLastLoginTime(user.uid);
    
    // Audit log: login success
    logAuditEventAsync(AuditActions.AUTH_LOGIN_SUCCESS, {
      metadata: { loginType },
    });
    
    setSuccess("Login successful! Redirecting...");
    setTimeout(() => navigate("/dashboard"), 1000);
    return true;
    
  } catch (error) {
    console.error("Error in firstLogin:", error);
    // Still log success since Firebase auth succeeded (just Firestore update failed)
    logAuditEventAsync(AuditActions.AUTH_LOGIN_SUCCESS, {
      metadata: { loginType, warning: 'firestore_update_failed' },
    });
    setSuccess("Login successful! Redirecting...");
    setTimeout(() => navigate("/dashboard"), 1000);
    return true;
  }
};
